import { useEffect, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { IntlProvider } from 'react-intl';
import { DEFAULT_LOCALE, dirFor, type Locale } from './locale.ts';
import { getMessages } from './messages.ts';
import { PrefsCtx, defaultPrefs, type LocalePrefs } from './prefs.ts';

const useIsoLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

export interface I18nProviderProps {
  prefs?: Partial<LocalePrefs> & { locale: Locale };
  children: ReactNode;
  onError?: (err: unknown) => void;
}

/** Wraps react-intl and exposes digit/calendar preferences. Sets `dir`/`lang` on <html> in the browser. */
export function I18nProvider({ prefs, children, onError }: I18nProviderProps) {
  const merged = useMemo<LocalePrefs>(() => ({ ...defaultPrefs(prefs?.locale), ...prefs }), [prefs]);
  useIsoLayoutEffect(() => {
    document.documentElement.lang = merged.locale;
    document.documentElement.dir = dirFor(merged.locale);
  }, [merged.locale]);
  return (
    <PrefsCtx.Provider value={merged}>
      <IntlProvider
        locale={merged.locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={getMessages(merged.locale)}
        textComponent="span"
        onError={onError ?? (() => undefined)}
      >
        {children}
      </IntlProvider>
    </PrefsCtx.Provider>
  );
}
